import React, { useState, useCallback } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { GitBranch, Pencil, Check, Trash2, Plus } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

const OPERATOR_LABELS = {
  equals: "Equals",
  not_equals: "Not equals",
  contains: "Contains",
  not_contains: "Does not contain",
  starts_with: "Starts with",
  ends_with: "Ends with",
  greater: "Greater than",
  greater_than_equals: "Greater than or equals",
  less: "Less than",
  less_than_equals: "Less than or equals",
  is_empty: "Is empty",
  is_not_empty: "Is not empty",
};

const VALUE_HIDDEN_OPS = ["is_empty", "is_not_empty"];

const emptyCondition = () => ({ variable: "", operator: "equals", value: "", dataType: "auto" });

/* ── Collapsed View ───────────────────────────────────────────── */
const CollapsedView = ({ data, onEdit }) => {
  const conditions = data.conditions || [];
  const logicBetween = data.logicBetween || [];

  return (
    <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: "#FEF9C3", padding: "12px 12px 8px", minWidth: "240px", maxWidth: "340px" }}>
      <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg" title="Edit">
        <Pencil size={12} className="text-yellow-700" />
      </button>

      <div className="flex items-center gap-2 mb-2">
        <GitBranch size={16} className="text-yellow-700" />
        <span className="text-[12px] font-bold text-yellow-900">Condition</span>
      </div>

      {conditions.length === 0 ? (
        <p className="text-[10px] text-yellow-600 italic">No conditions set</p>
      ) : (
        <div className="space-y-1">
          {conditions.map((cond, i) => (
            <React.Fragment key={i}>
              <div className="text-[9px] text-yellow-800 bg-yellow-50 px-2 py-1 rounded truncate">
                {cond.variable ? (
                  <span className="font-mono font-semibold">{`{{${cond.variable}}}`}</span>
                ) : (
                  <span className="italic text-yellow-500">no variable</span>
                )}
                {" "}
                <span className="font-semibold">{OPERATOR_LABELS[cond.operator] || cond.operator}</span>
                {!VALUE_HIDDEN_OPS.includes(cond.operator) && (
                  <> <span className="text-yellow-700">{cond.value || "..."}</span></>
                )}
              </div>
              {i < conditions.length - 1 && (
                <div className="text-center">
                  <span className={`inline-block text-[8px] font-bold uppercase px-2 py-0.5 rounded-full ${logicBetween[i] === "or" ? "bg-orange-100 text-orange-600" : "bg-yellow-200 text-yellow-700"}`}>
                    {logicBetween[i] === "or" ? "OR" : "AND"}
                  </span>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Expanded Editor ──────────────────────────────────────────── */
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [conditions, setConditions] = useState(() =>
    data.conditions && data.conditions.length > 0
      ? JSON.parse(JSON.stringify(data.conditions))
      : [emptyCondition()]
  );
  const [logicBetween, setLogicBetween] = useState(() => {
    const lb = data.logicBetween ? [...data.logicBetween] : [];
    while (lb.length < Math.max(0, (data.conditions || []).length - 1)) lb.push("and");
    return lb;
  });
  const [saveError, setSaveError] = useState(null);

  const updateCondition = (idx, field, value) => {
    setConditions(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setSaveError(null);
  };

  const addCondition = () => {
    setConditions(prev => [...prev, emptyCondition()]);
    setLogicBetween(prev => [...prev, "and"]);
  };

  const removeCondition = (idx) => {
    if (conditions.length <= 1) return;
    setConditions(prev => prev.filter((_, i) => i !== idx));
    setLogicBetween(prev => {
      const next = [...prev];
      if (idx === 0) next.shift();
      else next.splice(idx - 1, 1);
      return next;
    });
  };

  const updateLogic = (idx, value) => {
    setLogicBetween(prev => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const handleSave = () => {
    if (conditions.length === 0) { setSaveError("At least one condition is required"); return; }
    for (let i = 0; i < conditions.length; i++) {
      if (!conditions[i].variable?.trim()) { setSaveError(`Condition ${i + 1}: variable is required`); return; }
    }
    setSaveError(null);
    onSave({ conditions, logicBetween: logicBetween.slice(0, conditions.length - 1) });
  };

  return (
    <div className="nowheel" style={{ backgroundColor: "#FEF9C3", borderRadius: "12px", padding: "14px", minWidth: "380px", maxWidth: "460px" }} onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GitBranch size={16} className="text-yellow-700" />
          <span className="text-[13px] font-bold text-yellow-900">Condition</span>
        </div>
        <div className="flex gap-1.5">
          <button onClick={handleSave} className="px-3 py-1.5 text-[11px] font-bold bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-1 shadow-sm"><Check size={12} />Save</button>
          <button onClick={onCancel} className="px-3 py-1.5 text-[11px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
        </div>
      </div>

      {saveError && <p className="text-[9px] text-red-500 mb-2 font-semibold">{saveError}</p>}

      <NodeEditorTabs nodeType="condition">
      <div className="space-y-0 max-h-[420px] overflow-y-auto pr-1">
        {conditions.map((cond, ci) => (
          <React.Fragment key={ci}>
            <div className="bg-white rounded-lg shadow-sm p-2.5 mb-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-bold text-yellow-700">Condition {ci + 1}</span>
                <button onClick={() => removeCondition(ci)} disabled={conditions.length <= 1} className="p-0.5 text-[#667781] hover:text-red-500 disabled:opacity-30"><Trash2 size={10} /></button>
              </div>

              {/* Variable */}
              <div className="mb-1.5">
                <label className="text-[9px] font-semibold text-[#667781] uppercase">Variable</label>
                <VariableDropdown value={cond.variable || ""} onChange={name => updateCondition(ci, "variable", name)} flowVariables={flowVariables || []} allowCreate onCreateVariable={onCreateVariable} placeholder="Select variable..." />
              </div>

              {/* Operator + Data type row */}
              <div className="flex gap-1.5 mb-1.5">
                <div className="flex-1">
                  <label className="text-[9px] font-semibold text-[#667781] uppercase">Operator</label>
                  <select value={cond.operator || "equals"} onChange={e => updateCondition(ci, "operator", e.target.value)} className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none mt-0.5">
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not equals</option>
                    <option value="contains">Contains</option>
                    <option value="not_contains">Does not contain</option>
                    <option value="starts_with">Starts with</option>
                    <option value="ends_with">Ends with</option>
                    <option value="greater">Greater than</option>
                    <option value="greater_than_equals">Greater than or equals</option>
                    <option value="less">Less than</option>
                    <option value="less_than_equals">Less than or equals</option>
                    <option value="is_empty">Is empty</option>
                    <option value="is_not_empty">Is not empty</option>
                  </select>
                </div>
                <div className="w-20">
                  <label className="text-[9px] font-semibold text-[#667781] uppercase">Type</label>
                  <select value={cond.dataType || "auto"} onChange={e => updateCondition(ci, "dataType", e.target.value)} className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none mt-0.5">
                    <option value="auto">Auto</option>
                    <option value="number">Number</option>
                    <option value="string">String</option>
                  </select>
                </div>
              </div>

              {/* Value (hidden for is_empty / is_not_empty) */}
              {!VALUE_HIDDEN_OPS.includes(cond.operator) && (
                <div>
                  <label className="text-[9px] font-semibold text-[#667781] uppercase">Value</label>
                  <input value={cond.value || ""} onChange={e => updateCondition(ci, "value", e.target.value)} placeholder="Comparison value" className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none placeholder:text-[#8696a0] mt-0.5" />
                </div>
              )}
            </div>

            {/* AND/OR between conditions */}
            {ci < conditions.length - 1 && (
              <div className="flex justify-center py-1">
                <select value={logicBetween[ci] || "and"} onChange={e => updateLogic(ci, e.target.value)} className="px-3 py-1 text-[10px] font-bold rounded-full border-0 focus:outline-none cursor-pointer" style={{ backgroundColor: logicBetween[ci] === "or" ? "#FED7AA" : "#FDE68A", color: logicBetween[ci] === "or" ? "#C2410C" : "#A16207" }}>
                  <option value="and">AND</option>
                  <option value="or">OR</option>
                </select>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <button onClick={addCondition} className="mt-2 text-[10px] text-yellow-700 font-bold hover:underline flex items-center gap-1"><Plus size={10} />Add Condition</button>

      </NodeEditorTabs>
      <div className="flex gap-2 mt-3">
        <button onClick={handleSave} className="flex-1 py-2 text-[12px] font-bold bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"><Check size={14} />Save</button>
        <button onClick={onCancel} className="flex-1 py-2 text-[12px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
      </div>
    </div>
  );
};

/* ── Main Node ────────────────────────────────────────────────── */
const ConditionNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("condition");

  const handleSave = useCallback((formData) => {
    if (data.onNodeDataChange) data.onNodeDataChange(id, { ...data, ...formData });
    closeEditor();
  }, [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle type="target" position={Position.Left} className="!w-3.5 !h-3.5 !bg-yellow-500 !border-2 !border-white !shadow-sm" style={{ left: -7 }} />
      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          presentation="fullscreen"
          onCancel={closeEditor}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
        />
      </FlowNodeEditorShell>
      <Handle type="source" position={Position.Right} id="true" className="!w-3.5 !h-3.5 !bg-green-500 !border-2 !border-white !shadow-sm" style={{ top: "33%", right: -7 }} />
      <Handle type="source" position={Position.Right} id="false" className="!w-3.5 !h-3.5 !bg-red-500 !border-2 !border-white !shadow-sm" style={{ top: "67%", right: -7 }} />
    </div>
  );
};

export default ConditionNode;
